import type { ExpressionSpecification } from 'mapbox-gl';
import type { IntlShape } from 'react-intl';
import { FONT_SIZE_100 } from '@alltrails/denali/tokens';
import type { TrailResult } from '../redux/reducer';
import { buildVariantExpression, type TrailheadPinVariant, type TrailheadVariantExpressions } from './buildTrailheadIconExpressions';
import { formatTrailLengthForPin } from './svgDefinitions/formatTrailLengthForPin';
import { dynamicPinMarkerVerticalOffset } from './svgDefinitions/svgMarkerComponents';
import type { TrailheadPinStyle } from './trailheadPinVariant';

/**
 * The pill pins for the trailhead-pill and guidepost-pill variants. Unlike the circle pins, one sprite per state stretches to fit
 * its `text-field` (`icon-text-fit`), the same way the trail pins do, so the label is the pin's width. The
 * label is the length of the one trail at a trailhead (ex: "3.2 mi") or the number of trails (ex: "3 trails").
 * Collapsed dots, the name layer, and the sort key are shared with the circle pins.
 */

const pillImageNames: Record<TrailheadPinVariant, string> = {
  closed: 'pill-label-closed',
  saved: 'pill-label-favorite',
  completed: 'pill-label-completed',
  default: 'pill-label'
};

// The sprite is drawn with four paint slots that `image` params can recolor per feature (see the
// `-closed`, `-favorite` and `-completed` art, whose heart, check or minus shares the body's slot).
// A hovered or selected pill inverts the glyph and body, and leaves the badge (color-4) as drawn.
const activePillImageParams = {
  params: {
    'color-1': 'hsl(0, 0%, 100%)', // glyph: black -> white
    'color-2': 'hsl(60, 4%, 11%)', // body: white -> black, and the badge's heart / check / minus with it
    'color-3': 'hsl(60, 4%, 11%)' // outline: blends into the body
  }
};

export const activePillTextColor = 'hsl(0, 0%, 100%)';

export function buildTrailheadPillIconImage(
  variantExpressions: TrailheadVariantExpressions,
  isActiveExpression: ExpressionSpecification,
  { iconPrefix }: TrailheadPinStyle
): ExpressionSpecification {
  const imageName = buildVariantExpression(variantExpressions, variant => `${iconPrefix}-${pillImageNames[variant]}`);
  return ['case', isActiveExpression, ['image', imageName, activePillImageParams], ['image', imageName]];
}

/**
 * `icon-text-fit` sizes the icon around the text, so the pill follows the text and the only offset is the
 * text's. Negative is up: it lifts the pill by half its height so the tip of the pill sits on the trailhead.
 * The icon is on the left of the pill, so the horizontal offset is what centers the pill on the trailhead.
 */
const pillTextOffset: [number, number] = [0, -dynamicPinMarkerVerticalOffset / FONT_SIZE_100];

export const buildTrailheadPillTextOffset = (): ExpressionSpecification => ['literal', pillTextOffset];

// The trailhead name sits below the tip of the pill, in ems.
const pillNameTextOffset: [number, number] = [0, 1];

export const buildTrailheadPillNameTextOffset = (): ExpressionSpecification => ['literal', pillNameTextOffset];

type PillLabelArgs = {
  displayMetric: boolean;
  intl: IntlShape;
};

/** One trail with a length shows the length, as the trail pins do. Anything else shows how many trails there are. */
export function getTrailheadPillLabel(trails: TrailResult[], { displayMetric, intl }: PillLabelArgs): string {
  if (trails.length === 1 && trails[0].length) {
    return formatTrailLengthForPin(intl, trails[0].length, displayMetric);
  }
  return intl.formatMessage({ id: 'trailhead.pin.count', defaultMessage: '{count} trails' }, { count: trails.length });
}

/**
 * Mapbox can't read the search results, so the label is resolved here and matched per trailhead id, the same
 * way the trail counts are. A trailhead with no trails in the results draws no label.
 */
export function buildTrailheadPillLabelExpression(trailsByTrailhead: Map<number, TrailResult[]>, labelArgs: PillLabelArgs): ExpressionSpecification {
  if (!trailsByTrailhead.size) {
    return ['literal', ''];
  }

  const labelExpression: ExpressionSpecification = ['match', ['id']];
  [...trailsByTrailhead.keys()]
    .sort((a, b) => a - b)
    .forEach(id => {
      labelExpression.push(id, getTrailheadPillLabel(trailsByTrailhead.get(id) ?? [], labelArgs));
    });
  labelExpression.push('');
  return labelExpression;
}
